import React, { useEffect, useState } from 'react'
import { Button, Dropdown, message, Modal, Space, Upload } from 'antd'
import type { MenuProps } from 'antd'
import { DownOutlined, UploadOutlined } from '@ant-design/icons'
import { UploadProps } from 'antd/es/upload'
import type { MenuInfo } from 'rc-menu/lib/interface'

interface ActionButtonsProps {
  onUnloadFromWD: () => void
  uploadProps: UploadProps
  onSendToSchedex: () => void
  onUnloadFromSchedex: () => void
  onReloadFromSchedex: () => void
  isSendToSchedexEnabled: boolean
  isUnloadFromSchedexEnabled: boolean
  isReloadFromSchedexEnabled: boolean
  onSetPlanned: () => void
  isSetPlannedEnabled: boolean
  onSetVehicle: () => void
  onSetPyramid: () => void
  onSendMail: () => void
  onSetDeliveryDate: () => void
  isSetVehicleEnabled: boolean
  isSetPyramidEnabled: boolean
  isSetDeliveryDateEnabled: boolean
  isSendMailEnabled: boolean
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  onUnloadFromWD,
  uploadProps,
  onSendToSchedex,
  onUnloadFromSchedex,
  onReloadFromSchedex,
  onSetPlanned,
  onSetVehicle,
  onSetPyramid,
  onSendMail,
  onSetDeliveryDate,
  isSendToSchedexEnabled,
  isUnloadFromSchedexEnabled,
  isReloadFromSchedexEnabled,
  isSetPlannedEnabled,
  isSetVehicleEnabled,
  isSetPyramidEnabled,
  isSetDeliveryDateEnabled,
  isSendMailEnabled,
}) => {
  const [isLockedSchedex, setIsLockedSchedex] = useState(false)
  const [schedexLockedTimer, setSchedexLockedTimer] = useState<number | null>(null)
  const [schedexRequestCount, setSchedexRequestCount] = useState<number>(0)
  const [isModalVisible, setIsModalVisible] = useState(false)

  useEffect(() => {
    const checkLockStatus = async () => {
      try {
        const response = await fetch('/api/schedex/lock/status')
        const data = await response.json()

        if (data.isLocked) {
          setIsLockedSchedex(true)
          setSchedexLockedTimer(data.timeout)
          setSchedexRequestCount(data.requestCount || 0)
        }
      } catch (error) {
        console.error('Ошибка проверки блокировки:', error)
        message.error('Ошибка проверки блокировки')
      }
    }

    checkLockStatus()

    const eventSource = new EventSource('/api/schedex/events/lock')

    eventSource.onmessage = event => {
      const data = JSON.parse(event.data)
      setIsLockedSchedex(data.isLocked)
      setSchedexRequestCount(data.requestCount || 0)

      if (data.isLocked && data.timeout) {
        setSchedexLockedTimer(data.timeout)
      } else {
        setSchedexLockedTimer(null)
      }
    }

    eventSource.onerror = () => {
      console.error('Ошибка подключения к SSE')
      eventSource.close()
    }

    return () => {
      eventSource.close()
    }
  }, [])

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>

    if (isLockedSchedex && schedexLockedTimer && schedexLockedTimer > 0) {
      interval = setInterval(() => {
        setSchedexLockedTimer(prev => {
          if (prev && prev > 0) {
            return prev - 1
          } else {
            clearInterval(interval)
            setIsLockedSchedex(false)
            setSchedexRequestCount(0)
            setIsModalVisible(false)
            return 0
          }
        })
      }, 1000)
    }

    return () => clearInterval(interval)
  }, [isLockedSchedex, schedexLockedTimer])

  const handleSendToSchedexClick = () => {
    if (isLockedSchedex && schedexLockedTimer && schedexLockedTimer > 0) {
      setIsModalVisible(true)
    } else {
      onSendToSchedex()
    }
  }

  const handleSetVehicleClick = () => {
    if (isLockedSchedex && schedexLockedTimer && schedexLockedTimer > 0) {
      setIsModalVisible(true)
    } else {
      onSetVehicle()
    }
  }

  const handleMenuClick = (e: MenuInfo) => {
    switch (e.key) {
      case 'sendToSchedex':
        handleSendToSchedexClick()
        break
      case 'unloadFromSchedex':
        onUnloadFromSchedex()
        break
      case 'reloadFromSchedex':
        onReloadFromSchedex()
        break
      case 'setPyramid':
        onSetPyramid()
        break
      case 'setDeliveryDate':
        onSetDeliveryDate()
        break
      case 'setPlanned':
        onSetPlanned()
        break
      case 'sendMail':
        onSendMail()
        break
      case 'setVehicle':
        handleSetVehicleClick()
        break
      default:
        break
    }
  }

  const menuItems: MenuProps['items'] = [
    {
      key: 'sendToSchedex',
      label: 'Отправить в Шедекс',
      disabled: !isSendToSchedexEnabled,
    },
    {
      key: 'unloadFromSchedex',
      label: 'Выгрузить из Шедекс',
      disabled: !isUnloadFromSchedexEnabled,
    },
    {
      key: 'reloadFromSchedex',
      label: 'Перевыгрузить из Шедекс',
      disabled: !isReloadFromSchedexEnabled,
    },
    {
      key: 'setPyramid',
      label: 'Изменить пирамиду и очередность',
      disabled: !isSetPyramidEnabled,
    },
    {
      key: 'setDeliveryDate',
      label: 'Изменить дату доставки',
      disabled: !isSetDeliveryDateEnabled,
    },
    {
      key: 'setPlanned',
      label: 'Запланировать',
      disabled: !isSetPlannedEnabled,
    },
    {
      key: 'setVehicle',
      label: 'Назначить экипаж',
      disabled: !isSetVehicleEnabled,
    },
    {
      key: 'sendMail',
      label: 'Отправить уведомление о переносе',
      disabled: !isSendMailEnabled,
    },
  ]

  const dropdownMenu: MenuProps = {
    items: menuItems,
    onClick: handleMenuClick,
  }

  return (
    <Space style={{ width: '100%' }} wrap>
      <Button onClick={onUnloadFromWD}>Выгрузить заказы из WD</Button>
      <Upload {...uploadProps}>
        <Button icon={<UploadOutlined />}>Загрузить CSV</Button>
      </Upload>
      <Dropdown menu={dropdownMenu} trigger={['click']}>
        <Button type="primary">
          Действия <DownOutlined />
        </Button>
      </Dropdown>

      <Modal
        title="Выгрузка в Шедекс недоступна"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsModalVisible(false)}>
            Закрыть
          </Button>,
        ]}
      >
        <p>Превышен лимит вызовов: лимит для загрузки заказов составляет 2 раза в 30 секунд.</p>
        {schedexRequestCount > 0 && (
          <p>
            Сделано запросов: <b>{schedexRequestCount}/2</b>
          </p>
        )}
        {schedexLockedTimer !== null && (
          <p>
            Попробуйте снова через <b>{schedexLockedTimer}</b> секунд.
          </p>
        )}
      </Modal>
    </Space>
  )
}

export default React.memo(ActionButtons)
